import React, { Component } from 'react';
import { Row, Col, Input, Nav, NavItem, NavLink, TabContent, TabPane, Button, Badge, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { ActiveInactiveStatusCombo, CommonHelper, StyledCard, getLanguageOptions } from '../../Base/Common/CommonUIComponents';
import { FormattedMessage, injectIntl } from 'react-intl';
import { getAPI, postAPI, deleteAPI } from '../../Base/API';
import { canUseEmailDesigner, isOperationsUser } from '../../Base/Authorization';
import classnames from 'classnames';
import { handleNotification } from '../../Base/Common/Notification';
import CustomSelect from '../../Base/Common/CustomSelect';
import StripoEditor from './StripoEditor';
import TemplateVersions from './TemplateVersions';
import { blankTemplateHtml, blankTemplateCss } from './StripoBlankTemplate';
import { EMAIL_TEMPLATE_TYPES } from '../../Base/Common/ReferenceDataFunctions';

//Subject help per template type; a type without an entry has no subject field.
//Tag notes: 1 = empty on the online purchase email, 2 = only filled on the online purchase email.
const CUSTOMER_AND_HOTEL_TAGS = [{ tag: 'FirstName' }, { tag: 'LastName' }, { tag: 'FullName' }, { tag: 'HotelName' }];
const CONTEXT_TAGS = [{ tag: 'VoucherTitle', note: 1 }, { tag: 'VoucherId', note: 1 }, { tag: 'ValidUntil', note: 1 }, { tag: 'CardAmount', note: 1 }, { tag: 'PurchaseNumber', note: 2 }];
const SUBJECT_HELP = {
    EmailBody: {
        intro: ['TemplateDetails.SubjectHelpTypology', 'TemplateDetails.SubjectHelpEmpty'],
        tags: [...CUSTOMER_AND_HOTEL_TAGS, ...CONTEXT_TAGS]
    },
    EmailBodyGift: {
        intro: ['TemplateDetails.SubjectHelpEmpty'],
        tags: [{ tag: 'From' }, { tag: 'To' }, ...CUSTOMER_AND_HOTEL_TAGS, ...CONTEXT_TAGS]
    }
};
const TAG_NOTE_MARKS = { 1: '¹', 2: '²' };

const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

//Old email templates keep the subject in the translations JSON: each language without one gets an empty "Subject", listed first, to fill in.
const withSubjectKeys = (byLanguage) => {
    if (!isPlainObject(byLanguage)) {
        return byLanguage;
    }

    return Object.keys(byLanguage).reduce((result, language) => {
        const texts = byLanguage[language];
        const hasSubject = isPlainObject(texts) && Object.keys(texts).some(key => key.toLowerCase() === 'subject');

        result[language] = isPlainObject(texts) && !hasSubject ? { Subject: '', ...texts } : texts;
        return result;
    }, {});
};

const isValidJson = (text) => {
    try {
        JSON.parse(text);
        return true;
    }
    catch (e) {
        return false;
    }
};


class TemplateDetails extends Component {
    constructor(props) {
        super(props);

        this.stripoEditorRef = React.createRef();

        this.state = {
            template: null,
            languageVariants: [],
            activeTab: 'templateHtml',
            addLanguageModal: false,
            newLanguageCode: null,
            versionsModal: false,
            designKey: 0
        };
    }

    componentDidMount() {
        if (this.props.match.params.id) {
            this.getTemplateDetails(this.props.match.params.id)
        }
        else {
            //New template mode: type and language are chosen on the form and the record is created on the first save.
            //Only one email template per type per hotel group is allowed, so the types the group already has are removed from the combo.
            //The list falls back to the base templates (HotelId = Guid.Empty) when the group has none of a type: those do not count as used.
            this.setState({
                template: { name: '', active: true, type: null, languageCode: null, templateHtml: '', translations: '{}' }
            }, () =>
                getAPI(result => {
                    const { data } = result;

                    if (data?.response) {
                        const groupTemplates = data.response.filter(t => t.hotelId && t.hotelId !== '00000000-0000-0000-0000-000000000000');
                        this.setState({ usedTypes: [...new Set(groupTemplates.map(t => t.type))] });
                    }
                }, '/api/Voucher/v1/Config/notificationTemplates')
            );
        }
    }

    getTemplateDetails = (templateId) => {
        this.setState({ block: true });

        getAPI(result => {
            const { data, error } = result;

            if (error) {
                var errorMessage = [];
                errorMessage.push({ message: error.message, stack: error.stack, messageType: 'danger' });
                this.setState({ error: errorMessage, block: false });

                return;
            }
            if (data) {
                const template = data.response?.[0];

                if (template && template.translations) {
                    const translations = JSON.parse(template.translations);
                    const isOldEmailTemplate = !!SUBJECT_HELP[template.type] && !template.templateJson;

                    template.translations = JSON.stringify(isOldEmailTemplate ? withSubjectKeys(translations) : translations, null, 4);
                }

                this.setState({ template, block: false }, this.getLanguageVariants);
            }
            else {
                this.setState({ template: null, block: false });
            }
        }, `/api/Voucher/v1/Config/notificationTemplates/${templateId}`);
    }

    //All records with the same name and type (and hotel) are language variants of this template
    getLanguageVariants = () => {
        const { template } = this.state;

        if (!template) {
            return;
        }

        getAPI(result => {
            const { data, error } = result;

            if (error) {
                var errorMessage = [];
                errorMessage.push({ message: error.message, stack: error.stack, messageType: 'danger' });
                this.setState({ error: errorMessage, block: false });

                return;
            }
            if (data) {
                const languageVariants = (data.response || [])
                    .filter(t => t.name === template.name && t.type === template.type && t.hotelId === template.hotelId)
                    .sort((a, b) => (a.languageCode || '').localeCompare(b.languageCode || ''));

                this.setState({ languageVariants });
            }
        }, '/api/Voucher/v1/Config/notificationTemplates');
    }

    //Soft-delete of one language (variant) or of the whole template (every language); deleting the last language deletes the template
    deleteTemplate = (variant, allLanguages, e) => {
        if (e) e.stopPropagation();

        const { languageVariants } = this.state;
        const deletesTemplate = allLanguages || languageVariants.length <= 1;

        const confirmMessage = deletesTemplate ?
            this.props.intl.formatMessage({ id: 'TemplateDetails.ConfirmDeleteTemplate' })
            : this.props.intl.formatMessage({ id: 'TemplateDetails.ConfirmDeleteLanguage' }, { language: variant.languageCode });

        if (!window.confirm(confirmMessage)) return;

        this.setState({ block: true }, () =>
            deleteAPI(result => {
                const { data, error } = result;

                if (error) {
                    this.setState({ error: [{ message: error.message, stack: error.stack, messageType: 'danger' }], block: false });
                    return;
                }
                if (data?.errors?.length > 0) {
                    handleNotification(data);
                    this.setState({ block: false });
                    return;
                }

                handleNotification(data, <FormattedMessage id={deletesTemplate ? 'TemplateDetails.TemplateDeleted' : 'TemplateDetails.LanguageDeleted'} />, <FormattedMessage id="generic.success" />);

                const deletedIds = (data?.response || []).map(t => t.id);
                const remaining = languageVariants.filter(v => !deletedIds.includes(v.id));

                if (deletesTemplate || remaining.length === 0) {
                    this.props.history.push('/VoucherTemplates');
                }
                else if (deletedIds.includes(this.state.template.id)) {
                    this.props.history.replace({ pathname: `/TemplateDetails/${remaining[0].id}` });
                    this.getTemplateDetails(remaining[0].id);
                }
                else {
                    this.setState({ block: false }, this.getLanguageVariants);
                }
            }, `/api/Voucher/v1/Config/voucherTemplate/${variant.id}${allLanguages ? '/all' : ''}`)
        );
    }

    selectLanguage = (variant) => {
        if (this.state.template && variant.id !== this.state.template.id) {
            this.props.history.replace({ pathname: `/TemplateDetails/${variant.id}` });
            this.getTemplateDetails(variant.id);
        }
    }

    saveTemplate = (e) => {
        if (e) e.preventDefault();
        this.setState({ block: true });

        const body = { ...this.state.template };

        //New templates are created with the blank Stripo skeleton (esd- markup) so the editor opens right after the first save
        if (!body.id && !body.templateJson) {
            body.templateJson = blankTemplateHtml;
            body.templateCss = blankTemplateCss;
        }

        this.postTemplate(body);
    }

    //Persists the design source only. TemplateHtml is not sent so the compiled html used on sends is untouched.
    saveDesign = () => {
        if (this.stripoEditorRef.current) {
            this.setState({ block: true }, () =>
                this.stripoEditorRef.current.saveDesign((designHtml, designCss) => {
                    if (designHtml == null) {
                        //Editor not ready; don't persist an empty design
                        this.setState({ block: false });
                        return;
                    }
                    const body = { ...this.state.template, templateJson: designHtml, templateCss: designCss };
                    delete body.templateHtml;

                    this.postTemplate(body);
                })
            );
        }
    }

    //Compiles the final send-ready html and saves everything. This version is used for all future email sends.
    compileAndPublish = () => {
        if (this.stripoEditorRef.current) {
            this.setState({ block: true }, () =>
                this.stripoEditorRef.current.saveDesign((designHtml, designCss) => {
                    if (designHtml == null) {
                        //Editor not ready; don't persist an empty design
                        this.setState({ block: false });
                        return;
                    }
                    this.stripoEditorRef.current.compileEmail((error, compiledHtml) => {
                        if (error) {
                            this.setState({ error: [{ message: error, messageType: 'danger' }], block: false });
                            return;
                        }

                        const body = { ...this.state.template, templateJson: designHtml, templateCss: designCss, templateHtml: compiledHtml };

                        this.postTemplate(body);
                    });
                })
            );
        }
    }

    postTemplate = (body, onSaved) => {
        //Translations are stored in a json column: a syntax error would only fail on the database
        if (body.translations != null && !isValidJson(body.translations)) {
            handleNotification({ errors: [{ message: <FormattedMessage id="TemplateDetails.InvalidTranslations" /> }] });
            this.setState({ block: false });
            return;
        }

        postAPI(result => {
            const { data, error } = result;

            if (error) {
                var errorMessage = [];
                errorMessage.push({ message: error.message, stack: error.stack, messageType: 'danger' });
                this.setState({ error: errorMessage, block: false });

                return;
            }
            if (data) {
                if (data.errors && data.errors.length > 0) {
                    handleNotification(data);
                    this.setState({ block: false });
                }
                else if (data.response?.length > 0) {
                    handleNotification(data, <FormattedMessage id="TemplateDetails.TemplateSaved" />, <FormattedMessage id="generic.success" />);

                    const saved = data.response[0];

                    if (onSaved) {
                        onSaved(saved);
                    }
                    else if (!this.state.template.id) {
                        //First save of a new template: adopt the created record
                        this.props.history.replace({ pathname: `/TemplateDetails/${saved.id}` });
                        this.getTemplateDetails(saved.id);
                    }
                    else {
                        this.setState(prevState => ({
                            template: {
                                ...prevState.template,
                                templateHtml: saved.templateHtml,
                                templateJson: saved.templateJson,
                                templateCss: saved.templateCss,
                                //The server stores a blank subject as null
                                subject: saved.subject
                            },
                            block: false
                        }), this.getLanguageVariants);
                    }
                }
                else this.setState({ block: false });
            }
            else this.setState({ block: false });
        }, `/api/Voucher/v1/Config/voucherTemplate`, { request: body });
    }

    //Same 5 languages used across the voucher app (card creation combo), minus the ones the template already has
    getAvailableLanguages = () => {
        const { languageVariants, template } = this.state;

        const usedCodes = languageVariants.map(v => (v.languageCode || '').toUpperCase());
        if (template?.languageCode) {
            usedCodes.push(template.languageCode.toUpperCase());
        }

        return getLanguageOptions().filter(lang => !usedCodes.includes((lang.value || '').toUpperCase()));
    }

    //Creates a new template record with the same name and type, copying the current design, and opens it.
    //The published html is not copied: the new language stays unpublished until "Compile and Publish" (empty, not omitted, since Template is required)
    //The subject is not copied: it is written in the current language and would be sent in the new one.
    addLanguage = () => {
        const { template, newLanguageCode } = this.state;

        if (!template?.id || !newLanguageCode) {
            return;
        }

        const body = { ...template, id: null, languageCode: newLanguageCode, templateHtml: '', subject: '' };

        this.setState({ block: true, addLanguageModal: false }, () =>
            this.postTemplate(body, (saved) => {
                this.props.history.replace({ pathname: `/TemplateDetails/${saved.id}` });
                this.getTemplateDetails(saved.id);
                this.setState({ newLanguageCode: null });
            })
        );
    }

    handleChange = (e) => {
        const { name, value } = e.target;

        this.setState(prevState => ({
            template: {
                ...prevState.template,
                [name]: value
            }
        }));
    };

    handleSelect = (name, combo) => {
        this.setState(prevState => ({
            template: {
                ...prevState.template,
                [name]: combo ? combo.value : null
            }
        }));
    }

    toggleTab = (tabName) => {
        this.setState({ activeTab: tabName });
    }

    toggleAddLanguageModal = () => {
        this.setState(prevState => ({ addLanguageModal: !prevState.addLanguageModal }));
    }

    toggleVersionsModal = () => {
        this.setState(prevState => ({ versionsModal: !prevState.versionsModal }));
    }

    //Loads a previous version into the editor. Nothing is persisted until Save Design.
    restoreVersion = (version) => {
        this.setState(prevState => ({
            template: {
                ...prevState.template,
                templateJson: version.templateJson,
                templateCss: version.templateCss
            },
            designKey: prevState.designKey + 1,
            versionsModal: false
        }));
    }

    render() {
        const { template, languageVariants, activeTab, addLanguageModal, newLanguageCode } = this.state;

        const isStripoMode = !!template?.templateJson;

        //Only the types in SUBJECT_HELP carry a subject. Stripo templates have a subject field per language variant, not offered where
        //it would never be used: base templates and variants without a language (the subject must match the email language).
        //Old templates (no templateJson) keep it in the translations JSON, under the "Subject" key of each language.
        const subjectHelp = SUBJECT_HELP[template?.type];
        const isBaseTemplate = template?.hotelId === '00000000-0000-0000-0000-000000000000';
        const hasSubject = !!subjectHelp && !isBaseTemplate && (!template.id || (isStripoMode && !!template.languageCode));
        const hasTranslationsSubject = !!subjectHelp && !!template?.id && !isStripoMode;
        const subject = template?.subject || '';

        const subjectHelpContent = subjectHelp ?
            <div>
                {subjectHelp.intro.map(id =>
                    <div key={id} className="mb-2">
                        <FormattedMessage id={id} />
                    </div>
                )}
                <div className="mb-1">
                    <FormattedMessage id="TemplateDetails.SubjectHelpTags" />
                </div>
                <ul className="pl-3 mb-2 small">
                    {subjectHelp.tags.map(t =>
                        <li key={t.tag}>
                            <code>{`{{${t.tag}}}`}</code> – <FormattedMessage id={`TemplateDetails.SubjectTag.${t.tag}`} />{t.note ? ` ${TAG_NOTE_MARKS[t.note]}` : ''}
                        </li>
                    )}
                </ul>
                <div className="small">
                    <FormattedMessage id="TemplateDetails.SubjectHelpVoucherOnly" />
                </div>
                <div className="small">
                    <FormattedMessage id="TemplateDetails.SubjectHelpOnlineOnly" />
                </div>
            </div>
        : null;

        //Only email templates of the hotel group can be deleted: never the base templates (HotelId = Guid.Empty) nor the Voucher type
        const canDelete = !!template?.id && EMAIL_TEMPLATE_TYPES.includes(template.type) && !!template.hotelId && !isBaseTemplate && canUseEmailDesigner();

        //Base templates are shared by every hotel group: outside Operations they are only viewed (no save, publish, delete nor editor)
        const readOnly = !!template?.id && isBaseTemplate && !isOperationsUser();

        const availableLanguages = this.getAvailableLanguages();

        //New templates are always email templates designed on Stripo; the Voucher (pdf) type keeps its own designer on /VoucherTemplates.
        //Only one email template per type per hotel group, so types already in use by the group are not offered.
        const templateTypes = EMAIL_TEMPLATE_TYPES
            .filter(type => !(this.state.usedTypes || []).includes(type))
            .map(type => ({ value: type, label: type }));

        return (
            <StyledCard icon={'fa-regular fa-file'} title={'navbar.TemplatesConfig'} error={this.state.error} block={this.state.block}>
                {template ?
                    <>
                        <div>
                            <Row className="mb-4 mt-2 align-items-center">
                                <Col sm={1}> <FormattedMessage id="generic.Name" /> </Col>
                                <Col sm={template.id ? 5 : 3}>
                                    <Input type="text"
                                        placeholder=""
                                        required
                                        maxLength="200"
                                        name="name"
                                        value={template.name}
                                        onChange={this.handleChange}
                                        readOnly={readOnly}
                                    />
                                </Col>
                                {!template.id ?
                                    <>
                                        <Col sm={2}>
                                            <CustomSelect
                                                options={templateTypes}
                                                value={templateTypes.find(t => t.value === template.type) || ''}
                                                onChange={(combo) => this.handleSelect('type', combo)}
                                                placeholder={<FormattedMessage id="TemplateDetails.Type" />}
                                            />
                                        </Col>
                                        <Col sm={2}>
                                            <CustomSelect
                                                options={availableLanguages}
                                                value={availableLanguages.find(l => l.value === template.languageCode) || ''}
                                                onChange={(combo) => this.handleSelect('languageCode', combo)}
                                                placeholder={<FormattedMessage id="TemplateDetails.SelectLanguage" />}
                                            />
                                        </Col>
                                    </>
                                : ''}
                                <Col sm={1}> <FormattedMessage id="generic.status" /> </Col>
                                <Col sm={template.id ? 3 : 2}>
                                    <ActiveInactiveStatusCombo
                                        name='active'
                                        isClearable={!readOnly}
                                        isDisabled={readOnly}
                                        onChangeFunc={(name, combo) => this.handleSelect(name, combo)}
                                        placeholder={<FormattedMessage id="generic.status" />}
                                        value={template.active}
                                        required
                                    />
                                </Col>
                                <Col className="text-right">
                                    {canDelete ?
                                        <Button color="danger" className="btn-sm mr-2" type="button" onClick={(e) => this.deleteTemplate(template, true, e)} title={this.props.intl.formatMessage({ id: 'TemplateDetails.DeleteTemplate' })}>
                                            <i className="fa-solid fa-trash-can" />
                                        </Button>
                                    : ''}
                                    {readOnly ?
                                        <Badge color="secondary" className="p-2"><i className="fa-solid fa-lock mr-1" /><FormattedMessage id="TemplateDetails.BaseTemplateReadOnly" /></Badge>
                                    : isStripoMode && template.id ?
                                        <>
                                            <Button className="btn-host btn-sm mr-2" type="button" onClick={this.toggleVersionsModal} title="Versions">
                                                <i className="fa-solid fa-clock-rotate-left" />
                                            </Button>
                                            <Button className="btn-host btn-sm mr-2" type="button" onClick={this.saveDesign} title="Save Design">
                                                <i className="fa-solid fa-floppy-disk" />
                                            </Button>
                                            <Button className="btn-host btn-sm" type="button" onClick={this.compileAndPublish} title="Compile and Publish">
                                                <i className="fa-solid fa-paper-plane" />
                                            </Button>
                                        </>
                                        :
                                        <>
                                            <Button className="btn-host btn-sm" type="button" onClick={this.saveTemplate} disabled={!template.id && (!template.name || !template.type || !template.languageCode)}>
                                                <i className="fa-solid fa-floppy-disk" />
                                            </Button>
                                        </>
                                    }
                                </Col>
                            </Row>

                            {!template.id ?
                                <Row className="mb-3">
                                    <Col>
                                        <small className="text-muted"><FormattedMessage id="TemplateDetails.StripoCreationNote" /></small>
                                    </Col>
                                </Row>
                            : ''}

                            {template.id && isStripoMode ?
                                <Row className="mb-3 align-items-center">
                                    <Col>
                                        <Nav tabs>
                                            {languageVariants.map((variant, index) =>
                                                <NavItem key={index}>
                                                    <NavLink className={classnames({ active: variant.id === template.id }, 'pointer')} onClick={() => this.selectLanguage(variant)}>
                                                        {variant.languageCode ?
                                                            <>
                                                                <span className={`flag-icon flag-icon-${variant.languageCode === 'EN' ? 'gb' : variant.languageCode.toLowerCase()} px-2 mr-1`} />
                                                                {variant.languageCode}
                                                            </>
                                                        : <FormattedMessage id="TemplateDetails.NoLanguage" />}
                                                        {variant.templateJson ? <Badge color="primary" className="ml-2">Stripo</Badge> : ''}
                                                        {canDelete ?
                                                            <i className="fa-solid fa-xmark ml-2 text-danger" role="button" onClick={(e) => this.deleteTemplate(variant, false, e)} title={this.props.intl.formatMessage({ id: 'TemplateDetails.DeleteLanguage' })} />
                                                        : ''}
                                                    </NavLink>
                                                </NavItem>
                                            )}
                                            {!readOnly ?
                                                <NavItem>
                                                    <NavLink className="pointer" onClick={this.toggleAddLanguageModal}>
                                                        <i className="fa-solid fa-plus mr-1" /><FormattedMessage id="TemplateDetails.AddLanguage" />
                                                    </NavLink>
                                                </NavItem>
                                            : ''}
                                        </Nav>
                                    </Col>
                                </Row>
                            : ''}

                            {hasSubject ?
                                <Row className="mb-3">
                                    <Col sm={1} className="pt-2">
                                        <FormattedMessage id="TemplateDetails.Subject" />
                                        <CommonHelper id="templateSubjectHelp" placement="right" help={subjectHelpContent} />
                                    </Col>
                                    <Col sm={11}>
                                        <Input
                                            type="text"
                                            name="subject"
                                            maxLength={100}
                                            value={subject}
                                            onChange={this.handleChange}
                                        />
                                        <div className="d-flex justify-content-end pt-1">
                                            <span className="font_size_xxs text-muted">{subject.length}/100</span>
                                        </div>
                                    </Col>
                                </Row>
                            : ''}

                            {hasTranslationsSubject ?
                                <Row className="mb-3">
                                    {/* d-flex keeps the help icon next to the text (CommonHelper floats right) */}
                                    <Col className="d-flex align-items-center">
                                        <small className="text-muted">
                                            <FormattedMessage id="TemplateDetails.SubjectInTranslations" />
                                        </small>
                                        <CommonHelper id="templateTranslationsSubjectHelp" placement="right" help={subjectHelpContent} />
                                    </Col>
                                </Row>
                            : ''}

                            {!template.id ? '' : isStripoMode ?
                                <Row>
                                    <Col md={activeTab === 'translations' ? 6 : 12}>
                                        <Nav tabs>
                                            <NavItem>
                                                <NavLink className={classnames({ active: activeTab === 'templateHtml' })} onClick={() => this.toggleTab('templateHtml')}>
                                                    <FormattedMessage id="TemplateDetails.Design" />
                                                </NavLink>
                                            </NavItem>
                                            <NavItem >
                                                <NavLink className={classnames({ active: activeTab === 'translations' })} onClick={() => this.toggleTab('translations')}>
                                                    <FormattedMessage id="TemplateDetails.Translations" />
                                                </NavLink>
                                            </NavItem>
                                        </Nav>

                                        <div style={{ display: activeTab === 'templateHtml' ? 'block' : 'none' }}>
                                            {readOnly ?
                                                <iframe title="templatePreview" sandbox="" className="template-preview-frame" srcDoc={template.templateHtml} />
                                            :
                                                <StripoEditor
                                                    ref={this.stripoEditorRef}
                                                    templateId={template.id}
                                                    designKey={this.state.designKey}
                                                    html={template.templateJson}
                                                    css={template.templateCss}
                                                />
                                            }
                                        </div>
                                        {activeTab === 'translations' ?
                                            <Input
                                                type="textarea"
                                                data-type="json"
                                                name="translations"
                                                style={{ height: '65vh' }}
                                                value={template.translations}
                                                onChange={this.handleChange}
                                                readOnly={readOnly}
                                                required
                                            />
                                        : ''}
                                    </Col>
                                </Row>
                                :
                                <Row>
                                    <Col md={6}>
                                        <Nav tabs>
                                            <NavItem>
                                                <NavLink className={classnames({ active: activeTab === 'templateHtml' })} onClick={() => this.toggleTab('templateHtml')}>
                                                    Template Html
                                                </NavLink>
                                            </NavItem>
                                            <NavItem >
                                                <NavLink className={classnames({ active: activeTab === 'translations' })} onClick={() => this.toggleTab('translations')}>
                                                    <FormattedMessage id="TemplateDetails.Translations" />
                                                </NavLink>
                                            </NavItem>
                                        </Nav>

                                        <TabContent activeTab={this.state.activeTab}>
                                            <TabPane tabId="templateHtml" style={{ backgroundColor: "#ffffff" }}>
                                                <Input
                                                    type="textarea"
                                                    data-type="html"
                                                    name="templateHtml"
                                                    style={{ height: '65vh' }}
                                                    value={template.templateHtml}
                                                    onChange={this.handleChange}
                                                    readOnly={readOnly}
                                                    required
                                                />
                                            </TabPane>
                                            <TabPane tabId="translations" style={{ backgroundColor: "#ffffff" }}>
                                                <Input
                                                    type="textarea"
                                                    data-type="json"
                                                    name="translations"
                                                    style={{ height: '65vh' }}
                                                    value={template.translations}
                                                    onChange={this.handleChange}
                                                    readOnly={readOnly}
                                                    required
                                                />
                                            </TabPane>
                                        </TabContent>
                                    </Col>

                                    <Col md={6}>
                                        <h6 className="my-2 pb-1"><FormattedMessage id="VoucherPreview.VoucherPreview" /></h6>
                                        <iframe
                                            title="templatePreview"
                                            sandbox="allow-same-origin"
                                            style={{
                                                border: '1px solid #ccc',
                                                height: '65vh',
                                                width: '100%',
                                                backgroundColor: '#f8f9fa'
                                            }}
                                            srcDoc={template.templateHtml}
                                        />
                                    </Col>
                                </Row>
                            }
                        </div>

                        {this.state.versionsModal ?
                            <TemplateVersions
                                isOpen={this.state.versionsModal}
                                toggle={this.toggleVersionsModal}
                                templateId={template.id}
                                onRestore={this.restoreVersion}
                            />
                        : ''}

                        <Modal isOpen={addLanguageModal} toggle={this.toggleAddLanguageModal}>
                            <ModalHeader toggle={this.toggleAddLanguageModal}>
                                <FormattedMessage id="TemplateDetails.AddLanguage" />
                            </ModalHeader>
                            <ModalBody>
                                <Row className="align-items-center">
                                    <Col>
                                        <CustomSelect
                                            options={availableLanguages}
                                            value={availableLanguages.find(l => l.value === newLanguageCode) || ''}
                                            onChange={(combo) => this.setState({ newLanguageCode: combo ? combo.value : null })}
                                            placeholder={<FormattedMessage id="TemplateDetails.SelectLanguage" />}
                                            isClearable
                                        />
                                    </Col>
                                    <Col className="col-3 text-right">
                                        <Button className="btn-host btn-sm" onClick={this.addLanguage} disabled={!newLanguageCode}>
                                            <FormattedMessage id="generic.Add" />
                                        </Button>
                                    </Col>
                                </Row>
                            </ModalBody>
                        </Modal>
                    </>
                : ''}
            </StyledCard>
        );
    }
}

export default injectIntl(TemplateDetails);
